"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import PharmacyList from "../../components//PharmacyList/PharmacyList";
import { useTour } from "../../../appointments/hooks/useTour";
import { pharmacyTourSteps } from "./pharmacyTourConfig";

import usePlaces from "../../hooks/usePlaces";
import "./Pharmacy.css";

// Map tab label -> backend category
const tabToCategory = (tab) => {
  switch (tab) {
    case "Pharmacy":
      return "pharmacies";
    case "Parapharmacy":
      return "parapharmacies";
    case "OnCall":
      return "on_call_pharmacies";
    case "Night":
      return "night_pharmacies";
    default:
      return "pharmacies";
  }
};

export default function Pharma({ LayoutComponent = AppLayoutDoctor }) {
	  const { t } = useTranslation();
	  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [activeTab, setActiveTab] = useState("Pharmacy");
  const [searchQuery, setSearchQuery] = useState("");

  const { items, loading, error, loadCategory, selectPlace } = usePlaces();

  // Start guided tour if requested via URL
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "pharmacy";
    if (hasGuide || hasTour) {
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        const list = document.querySelector(".pharmacy-list-scroll");
        if (searchInput && list) setShouldStartTour(true);
        else setTimeout(() => setShouldStartTour(true), 1200);
      }, 600);
    }
  }, [searchParams]);

  useTour(shouldStartTour, pharmacyTourSteps);

  // Load category when tab changes
  useEffect(() => {
    const category = tabToCategory(activeTab);
    loadCategory(category, { limit: 50, skip: 0 });
  }, [activeTab, loadCategory]);

  // Local filtering by name/address/vicinity
  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      (p?.name || "").toLowerCase().includes(q) ||
      (p?.address || "").toLowerCase().includes(q) ||
      (p?.vicinity || "").toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

	  return (
    <LayoutComponent>
      <main className="pharmacy-main pharmacy--listOnly">
	        <div className="pharmacy-container">
	          <PharmacyHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            showTabs
	            title={t("locators.pharmacy.title")}
	            subtitle={t("locators.pharmacy.subtitle")}
            enableFilters={true}
          />

          <section className="pharmacy-list-card">
	            <div className="pharmacy-list-scroll">
	              {loading && (
	                <p className="pharmacy-empty">
	                  {t("locators.common.loadingPlaces")}
	                </p>
	              )}
	              {!loading && error && (
	                <p className="pharmacy-empty">
	                  {t("locators.common.failedPlaces", { error: String(error) })}
	                </p>
	              )}
	              {!loading && !error && filtered.length === 0 ? (
	                <p className="pharmacy-empty">{t("locators.common.noResults")}</p>
	              ) : (
                <PharmacyList
                  pharmacies={filtered}
                  onPharmacyClick={(p) => selectPlace(p)}
                  selectedPharmacy={null}
                />
              )}
            </div>
          </section>
        </div>
      </main>
    </LayoutComponent>
  );
}
